/**
 * בקשה שנקטעה כי המשתמשת יצאה מהאפליקציה — נשלחת שוב אוטומטית כשהיא חוזרת.
 *
 * ⚠️ iOS עוצר בקשות רשת של דף/‏PWA שעבר לרקע, והבקשה נכשלת כ"שגיאת רשת" (נמדד 28.09.2026:
 * בר שלח צילום, עבר לוואטסאפ, חזר — "אין חיבור לאינטרנט", למרות וויפי תקין; כשנשאר
 * באפליקציה זה עבד). ניתוח AI לוקח 7–17 שניות, בדיוק הזמן שבו מציצים בהודעה. עד היום זה
 * הוצג כשגיאה והיא התחילה מחדש ידנית — כנראה מקור מרכזי ל"נכשל שוב ושוב".
 *
 * בטוח רק לבקשות בלי תופעות לוואי (ניתוח AI — לא שמירה): אם השרת כן סיים, שליחה חוזרת
 * רק מנתחת שוב.
 */

export interface Visibility {
  hidden(): boolean;
  /** מאזין לשינוי; מחזיר פונקציית ניתוק */
  onChange(cb: () => void): () => void;
}

export const docVisibility: Visibility = {
  hidden: () => typeof document !== 'undefined' && document.visibilityState === 'hidden',
  onChange: (cb) => {
    if (typeof document === 'undefined') return () => {};
    document.addEventListener('visibilitychange', cb);
    return () => document.removeEventListener('visibilitychange', cb);
  },
};

function waitVisible(vis: Visibility): Promise<void> {
  if (!vis.hidden()) return Promise.resolve();
  return new Promise((resolve) => {
    const off = vis.onChange(() => {
      if (vis.hidden()) return;
      off();
      resolve();
    });
  });
}

/** כמה פעמים ממשיכים אחרי חזרה — מי שיוצאת שוב ושוב תקבל בסוף הודעה, לא לולאה */
const MAX_RESUMES = 2;

/**
 * מריץ את `attempt`. כשל שהוא `isInterruption` **ובזמנו הדף היה ברקע** → ממתינים לחזרה
 * ומנסים שוב. כשל כזה שחוזר אחרי MAX_RESUMES נזרק כ-`interrupted`; כל כשל אחר — כמו שהוא.
 */
export async function retryAfterResume<T>(
  attempt: () => Promise<T>,
  isInterruption: (e: unknown) => boolean,
  vis: Visibility = docVisibility,
): Promise<T> {
  for (let resumes = 0; ; resumes++) {
    let wentHidden = vis.hidden();
    const off = vis.onChange(() => { if (vis.hidden()) wentHidden = true; });
    try {
      return await attempt();
    } catch (e) {
      // כשל רשת כשהדף היה גלוי כל הזמן = באמת אין רשת; לא ממתינים ולא מסתירים אותו
      if (!isInterruption(e) || !(wentHidden || vis.hidden())) throw e;
      if (resumes >= MAX_RESUMES) throw new Error('interrupted');
    } finally {
      off();
    }
    await waitVisible(vis);
  }
}
